import React, { useEffect, useMemo, useState } from "react";
import API from "../../../util/API";
import {
  CreditCard,
  RefreshCcw,
  Save,
  FlaskConical,
  ShieldCheck,
  Bug,
  Activity,
  Plus,
  X,
  ImagePlus,
  Loader2,
  Trash2,
} from "lucide-react";
import { ALL_COUNTRY_OPTIONS } from "../../../util/allCountries";

const cx = (...arr) => arr.filter(Boolean).join(" ");
const HIDDEN_PROVIDERS = new Set(["bkash"]);
const countryNameByCode = new Map(ALL_COUNTRY_OPTIONS.map((country) => [country.code, country.name]));

const parseCommaList = (value) =>
  String(value || "")
    .split(",")
    .map((x) => String(x || "").trim().toUpperCase())
    .filter(Boolean);

const toCommaList = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");

const pretty = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const truncate = (value, n = 160) => {
  const text = String(value || "");
  if (text.length <= n) return text;
  return `${text.slice(0, n)}...`;
};

const countryLabel = (code) => countryNameByCode.get(String(code || "").toUpperCase()) || String(code || "").toUpperCase();

const GATEWAY_ICON_MAX_SOURCE_BYTES = 4 * 1024 * 1024;
const GATEWAY_ICON_MAX_DATA_URL_BYTES = 80 * 1024;
const GATEWAY_ICON_SIZES = [112, 96, 80, 64, 48];
const GATEWAY_ICON_QUALITIES = [0.82, 0.72, 0.62, 0.52];

const byteLength = (value) => new Blob([String(value || "")]).size;

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read this image."));
    };
    img.src = url;
  });

const canvasSupportsWebp = (() => {
  if (typeof document === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    return false;
  }
})();

const compressGatewayIcon = async (file) => {
  if (!file) return "";
  if (!/^image\/(png|jpe?g|webp)$/i.test(file.type || "")) {
    throw new Error("Upload a PNG, JPG, or WebP icon.");
  }
  if (file.size > GATEWAY_ICON_MAX_SOURCE_BYTES) {
    throw new Error("Icon source is too large. Use an image under 4 MB.");
  }

  const img = await loadImageFromFile(file);
  const mime = canvasSupportsWebp ? "image/webp" : "image/png";
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not compress this image.");

  let best = "";
  for (const maxSide of GATEWAY_ICON_SIZES) {
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
    canvas.width = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
    canvas.height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    for (const quality of GATEWAY_ICON_QUALITIES) {
      const dataUrl = canvas.toDataURL(mime, quality);
      if (!best || byteLength(dataUrl) < byteLength(best)) best = dataUrl;
      if (byteLength(dataUrl) <= GATEWAY_ICON_MAX_DATA_URL_BYTES) return dataUrl;
    }
  }

  if (best && byteLength(best) <= GATEWAY_ICON_MAX_DATA_URL_BYTES) return best;
  throw new Error("Icon could not be compressed enough. Use a simpler image.");
};

const gatewayIconSizeLabel = (value) => {
  const bytes = byteLength(value);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const sortCountryCodes = (codes) =>
  [...codes].sort((a, b) => countryLabel(a).localeCompare(countryLabel(b)));

const sanitizeCredentials = (credentials = {}) =>
  Object.fromEntries(
    Object.entries(credentials || {}).filter(
      ([, value]) => String(value ?? "").trim() !== "__configured__"
    )
  );

const normalizeConfigForDraft = (config = {}) => ({
  installed: Boolean(config.installed),
  enabled: Boolean(config.enabled),
  sandbox: Boolean(config.sandbox),
  simulationMode: Boolean(config.simulationMode),
  displayName: String(config.displayName || ""),
  description: String(config.description || ""),
  iconUrl: String(config.iconUrl || ""),
  allowedCountriesText: toCommaList(config.allowedCountries),
  allowedCurrenciesText: toCommaList(config.allowedCurrencies),
  credentials: { ...(config.credentials || {}) },
  checkoutOptions: {
    allowPopup: Boolean(config?.checkoutOptions?.allowPopup),
    allowRedirect: Boolean(config?.checkoutOptions?.allowRedirect),
    requireWebhooks: Boolean(config?.checkoutOptions?.requireWebhooks),
    require3DS: Boolean(config?.checkoutOptions?.require3DS),
    manualReviewThreshold: Number(config?.checkoutOptions?.manualReviewThreshold || 500),
  },
});

const buildPayloadFromDraft = (draft = {}, { forceInstalled = false } = {}) => ({
  installed: forceInstalled ? true : Boolean(draft.installed),
  enabled: Boolean(draft.enabled),
  sandbox: Boolean(draft.sandbox),
  simulationMode: Boolean(draft.simulationMode),
  displayName: String(draft.displayName || "").trim(),
  description: String(draft.description || "").trim(),
  iconUrl: String(draft.iconUrl || "").trim(),
  allowedCountries: parseCommaList(draft.allowedCountriesText),
  allowedCurrencies: parseCommaList(draft.allowedCurrenciesText),
  credentials: sanitizeCredentials(draft.credentials),
  checkoutOptions: {
    allowPopup: Boolean(draft?.checkoutOptions?.allowPopup),
    allowRedirect: Boolean(draft?.checkoutOptions?.allowRedirect),
    requireWebhooks: Boolean(draft?.checkoutOptions?.requireWebhooks),
    require3DS: Boolean(draft?.checkoutOptions?.require3DS),
    manualReviewThreshold: Number(draft?.checkoutOptions?.manualReviewThreshold || 500),
  },
});

export default function PaymentGatewayManagement() {
  const [definitions, setDefinitions] = useState({});
  const [configs, setConfigs] = useState({});
  const [drafts, setDrafts] = useState({});
  const [activeProvider, setActiveProvider] = useState("");
  const [activeSection, setActiveSection] = useState("gateways");
  const [editOpen, setEditOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingProvider, setSavingProvider] = useState("");
  const [testingProvider, setTestingProvider] = useState("");
  const [testResults, setTestResults] = useState({});

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addProvider, setAddProvider] = useState("");
  const [addDraft, setAddDraft] = useState(normalizeConfigForDraft({}));
  const [addSaving, setAddSaving] = useState(false);
  const [addTesting, setAddTesting] = useState(false);
  const [addResult, setAddResult] = useState(null);

  const orderedProviders = useMemo(
    () => Object.keys(definitions || {}).filter((p) => !HIDDEN_PROVIDERS.has(p)),
    [definitions]
  );
  const installedProviders = useMemo(
    () => orderedProviders.filter((p) => Boolean(configs?.[p]?.installed)),
    [orderedProviders, configs]
  );
  const addableProviders = useMemo(
    () => orderedProviders.filter((p) => !Boolean(configs?.[p]?.installed)),
    [orderedProviders, configs]
  );

  const activeDef = definitions?.[activeProvider] || {};
  const activeDraft = drafts?.[activeProvider] || normalizeConfigForDraft(configs?.[activeProvider] || {});
  const activeResult = testResults?.[activeProvider];

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [defsRes, cfgRes, sessionsRes, attemptsRes] = await Promise.all([
        API.get("/payment/admin/providers/definitions"),
        API.get("/payment/admin/providers"),
        API.get("/payment/admin/sessions?limit=25"),
        API.get("/payment/admin/attempts?limit=50"),
      ]);

      const defs = defsRes?.data?.definitions || {};
      const providerConfigs = cfgRes?.data?.providers || {};
      const nextDrafts = {};
      Object.keys(providerConfigs).forEach((provider) => {
        nextDrafts[provider] = normalizeConfigForDraft(providerConfigs[provider]);
      });

      setDefinitions(defs);
      setConfigs(providerConfigs);
      setDrafts(nextDrafts);
      setSessions(Array.isArray(sessionsRes?.data?.sessions) ? sessionsRes.data.sessions : []);
      setAttempts(Array.isArray(attemptsRes?.data?.attempts) ? attemptsRes.data.attempts : []);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load payment gateway settings."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (activeProvider) return;
    if (installedProviders.length) {
      setActiveProvider(installedProviders[0]);
      return;
    }
    if (orderedProviders.length) {
      setActiveProvider(orderedProviders[0]);
    }
  }, [activeProvider, installedProviders, orderedProviders]);

  const refreshSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await API.get("/payment/admin/sessions?limit=25");
      setSessions(Array.isArray(res?.data?.sessions) ? res.data.sessions : []);
    } finally {
      setSessionsLoading(false);
    }
  };

  const refreshAttempts = async () => {
    setAttemptsLoading(true);
    try {
      const res = await API.get("/payment/admin/attempts?limit=50");
      setAttempts(Array.isArray(res?.data?.attempts) ? res.data.attempts : []);
    } finally {
      setAttemptsLoading(false);
    }
  };

  const updateDraft = (provider, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [provider]: {
        ...(prev?.[provider] || {}),
        ...patch,
      },
    }));
  };

  const updateCredential = (provider, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [provider]: {
        ...(prev?.[provider] || {}),
        credentials: {
          ...(prev?.[provider]?.credentials || {}),
          [key]: value,
        },
      },
    }));
  };

  const updateCheckoutOption = (provider, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [provider]: {
        ...(prev?.[provider] || {}),
        checkoutOptions: {
          ...(prev?.[provider]?.checkoutOptions || {}),
          [key]: value,
        },
      },
    }));
  };

  const saveProvider = async (provider, draft, { asAdd = false } = {}) => {
    if (!provider || !draft) return;
    const payload = buildPayloadFromDraft(draft, { forceInstalled: true });
    if (asAdd) setAddSaving(true);
    else setSavingProvider(provider);

    try {
      const res = await API.put(`/payment/admin/providers/${provider}`, payload);
      const config = res?.data?.config;
      if (config) {
        setConfigs((prev) => ({ ...prev, [provider]: config }));
        setDrafts((prev) => ({
          ...prev,
          [provider]: normalizeConfigForDraft(config),
        }));
      }

      setTestResults((prev) => ({
        ...prev,
        [provider]: {
          ok: true,
          message: asAdd ? "Gateway added successfully." : "Configuration saved successfully.",
        },
      }));

      if (asAdd) {
        setActiveProvider(provider);
        setAddResult({ ok: true, message: "Gateway added to list." });
        setAddOpen(false);
      }
    } catch (e) {
      const message =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to save provider settings.";
      if (asAdd) {
        setAddResult({ ok: false, message });
      } else {
        setTestResults((prev) => ({
          ...prev,
          [provider]: { ok: false, message },
        }));
      }
    } finally {
      if (asAdd) setAddSaving(false);
      else setSavingProvider("");
    }
  };

  const testProvider = async (provider, draft, { fromAdd = false } = {}) => {
    if (!provider || !draft) return;
    const payload = buildPayloadFromDraft(draft, { forceInstalled: true });
    if (fromAdd) setAddTesting(true);
    else setTestingProvider(provider);

    try {
      const res = await API.post(`/payment/admin/providers/${provider}/test`, payload);
      const ok = Boolean(res?.data?.success);
      const detail = res?.data?.testResult?.status || "ok";
      const result = { ok, message: ok ? `Test passed (${detail})` : "Test failed" };
      if (fromAdd) setAddResult(result);
      else {
        setTestResults((prev) => ({ ...prev, [provider]: result }));
      }
    } catch (e) {
      const result = {
        ok: false,
        message:
          e?.response?.data?.message ||
          e?.message ||
          "Gateway test failed.",
      };
      if (fromAdd) setAddResult(result);
      else {
        setTestResults((prev) => ({ ...prev, [provider]: result }));
      }
    } finally {
      if (fromAdd) setAddTesting(false);
      else setTestingProvider("");
    }
  };

  const openAddModal = () => {
    const first = addableProviders[0] || orderedProviders[0] || "";
    if (!first) return;
    setAddProvider(first);
    setAddDraft(normalizeConfigForDraft(drafts?.[first] || configs?.[first] || {}));
    setAddResult(null);
    setAddOpen(true);
  };

  const openEditModal = (provider) => {
    if (!provider) return;
    setActiveProvider(provider);
    setEditOpen(true);
  };

  const onChangeAddProvider = (nextProvider) => {
    setAddProvider(nextProvider);
    setAddDraft(normalizeConfigForDraft(drafts?.[nextProvider] || configs?.[nextProvider] || {}));
    setAddResult(null);
  };

  const updateAddDraft = (patch) => {
    setAddDraft((prev) => ({ ...prev, ...patch }));
  };

  const updateAddCredential = (key, value) => {
    setAddDraft((prev) => ({
      ...prev,
      credentials: {
        ...(prev?.credentials || {}),
        [key]: value,
      },
    }));
  };

  const updateAddCheckoutOption = (key, value) => {
    setAddDraft((prev) => ({
      ...prev,
      checkoutOptions: {
        ...(prev?.checkoutOptions || {}),
        [key]: value,
      },
    }));
  };

  const installedRows = installedProviders.map((provider) => ({
    provider,
    def: definitions?.[provider] || {},
    cfg: configs?.[provider] || {},
  }));

  return (
    <div className="">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-900">Payment Gateway Management</h1>
            <p className="mt-1 text-sm text-slate-600">
              Add gateway from popup, verify credentials, then manage sandbox/enable and country rules.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadAll}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
            >
              <RefreshCcw size={14} />
              Reload
            </button>
          </div>
        </div>
        {error ? (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <SectionTab
            active={activeSection === "gateways"}
            onClick={() => setActiveSection("gateways")}
            icon={<CreditCard size={14} />}
            label="Gateways"
          />
          <SectionTab
            active={activeSection === "transactions"}
            onClick={() => setActiveSection("transactions")}
            icon={<Activity size={14} />}
            label="Transactions"
          />
          <SectionTab
            active={activeSection === "logs"}
            onClick={() => setActiveSection("logs")}
            icon={<Bug size={14} />}
            label="Logs"
          />
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-600">
          Loading payment gateway settings...
        </div>
      ) : (
        <>
          {activeSection === "gateways" ? (
          <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Gateways</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Add developed gateways and control billing-country availability.
                </p>
              </div>
              <button
                type="button"
                onClick={openAddModal}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#D2042D] px-3 py-2 text-xs font-semibold text-white hover:bg-[#b80326]"
              >
                <Plus size={13} />
                Add New Gateway
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Provider</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Display</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Enabled</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Sandbox</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Countries</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Currencies</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {!installedRows.length ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-sm text-slate-500 text-center">
                        No gateway added yet. Click <strong>Add Gateway</strong>.
                      </td>
                    </tr>
                  ) : (
                    installedRows.map(({ provider, cfg, def }) => (
                      <tr key={provider} className="border-t border-slate-100">
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                          <div className="flex items-center gap-2">
                            <GatewayIconPreview iconUrl={cfg?.iconUrl} />
                            <span>{def?.label || provider}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">{cfg?.displayName || "-"}</td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={cx(
                              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold",
                              cfg?.enabled
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-slate-200 bg-slate-50 text-slate-700"
                            )}
                          >
                            <ShieldCheck size={12} />
                            {cfg?.enabled ? "Enabled" : "Disabled"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">{cfg?.sandbox ? "Yes" : "No"}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          {Array.isArray(cfg?.allowedCountries) ? cfg.allowedCountries.join(", ") || "-" : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          {Array.isArray(cfg?.allowedCurrencies) ? cfg.allowedCurrencies.join(", ") || "-" : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            type="button"
                            onClick={() => openEditModal(provider)}
                            className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-slate-50"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
          ) : null}

          {false && !!activeProvider && (
            <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{activeProvider}</p>
                  <h2 className="mt-1 text-lg font-bold text-slate-900">
                    Edit Gateway: {activeDef?.label || activeProvider}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Update sandbox/live config, credentials, and availability rules.
                  </p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                  <CreditCard size={13} />
                  {activeDef?.category || "gateway"}
                </div>
              </div>

              <div className="p-5 space-y-4">
                <GatewayForm
                  def={activeDef}
                  draft={activeDraft}
                  onPatch={(patch) => updateDraft(activeProvider, patch)}
                  onCredential={(key, value) => updateCredential(activeProvider, key, value)}
                  onCheckoutOption={(key, value) => updateCheckoutOption(activeProvider, key, value)}
                />

                {activeResult ? (
                  <div
                    className={cx(
                      "rounded-xl border px-4 py-3 text-sm",
                      activeResult.ok
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-rose-200 bg-rose-50 text-rose-700"
                    )}
                  >
                    {activeResult.message}
                  </div>
                ) : null}

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:justify-end">
                  <button
                    type="button"
                    onClick={() => testProvider(activeProvider, activeDraft)}
                    disabled={testingProvider === activeProvider}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <FlaskConical size={14} />
                    {testingProvider === activeProvider ? "Testing..." : "Test Credentials"}
                  </button>
                  <button
                    type="button"
                    onClick={() => saveProvider(activeProvider, activeDraft)}
                    disabled={savingProvider === activeProvider}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#D2042D] px-4 py-2 text-sm font-semibold text-white hover:bg-[#b80326] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Save size={14} />
                    {savingProvider === activeProvider ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {!loading && activeSection === "transactions" ? (
        <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Recent Payment Sessions</h3>
            <p className="mt-1 text-sm text-slate-600">Monitor live checkout sessions by gateway and status.</p>
          </div>
          <button
            type="button"
            onClick={refreshSessions}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
          >
            <RefreshCcw size={14} className={sessionsLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Session</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Provider</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Status</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Amount</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Risk</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Created</th>
              </tr>
            </thead>
            <tbody>
              {!sessions.length ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-sm text-slate-500 text-center">
                    No payment sessions found yet.
                  </td>
                </tr>
              ) : (
                sessions.map((s) => (
                  <tr key={s.sessionId} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-xs font-mono text-slate-700">{s.sessionId}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900">{String(s.provider || "").toUpperCase()}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                        <ShieldCheck size={12} className="text-emerald-600" />
                        {pretty(s.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {Number(s?.amount?.value || 0).toFixed(2)} {String(s?.amount?.currency || "").toUpperCase()}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{Number(s?.risk?.score || 0)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {s.createdAt ? new Date(s.createdAt).toLocaleString() : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </section>
      ) : null}

      {!loading && activeSection === "logs" ? (
        <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Transaction Error Logs</h3>
            <p className="mt-1 text-sm text-slate-600">
              See failure logs, error code, and non-200 gateway responses.
            </p>
          </div>
          <button
            type="button"
            onClick={refreshAttempts}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
          >
            <RefreshCcw size={14} className={attemptsLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Time</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Session</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Provider</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Step</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Status</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">HTTP</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Code</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">Report</th>
              </tr>
            </thead>
            <tbody>
              {!attempts.length ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-sm text-slate-500 text-center">
                    No transaction logs found.
                  </td>
                </tr>
              ) : (
                attempts.map((a) => {
                  const failed = String(a?.status || "").toLowerCase() === "failed";
                  const report =
                    a?.errorMessage ||
                    a?.responseMeta?.message ||
                    a?.responseMeta?.statusFromWebhook ||
                    a?.responseMeta?.status ||
                    JSON.stringify(a?.responseMeta || {});

                  return (
                    <tr
                      key={`${a._id || ""}-${a.createdAt || ""}`}
                      className={cx(
                        "border-t",
                        failed ? "border-rose-100 bg-rose-50/40" : "border-slate-100"
                      )}
                    >
                      <td className="px-4 py-3 text-xs text-slate-700 whitespace-nowrap">
                        {a.createdAt ? new Date(a.createdAt).toLocaleString() : "-"}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-700">{a.sessionId || "-"}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900">{String(a.provider || "").toUpperCase()}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{pretty(a.type)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={cx(
                            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold",
                            failed
                              ? "border-rose-200 bg-rose-50 text-rose-700"
                              : "border-slate-200 bg-slate-50 text-slate-700"
                          )}
                        >
                          <Activity size={11} />
                          {pretty(a.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{a.httpStatus ?? "-"}</td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-700">{a.errorCode || "-"}</td>
                      <td className="px-4 py-3 text-xs text-slate-700 max-w-[420px]">
                        <span className="inline-flex items-center gap-1 mr-1 text-slate-500">
                          <Bug size={11} />
                        </span>
                        {truncate(report, 180)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        </section>
      ) : null}

      {addOpen ? (
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Add Gateway</p>
                <h3 className="mt-1 text-lg font-bold text-slate-900">New Gateway Setup</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Enter credentials, run test, then add gateway to list.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white p-2 text-slate-700 hover:bg-slate-50"
              >
                <X size={15} />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[76vh] overflow-y-auto">
              <label className="block">
                <span className="text-xs font-semibold text-slate-600">Gateway Provider</span>
                <select
                  value={addProvider}
                  onChange={(e) => onChangeAddProvider(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
                >
                  {(addableProviders.length ? addableProviders : orderedProviders).map((p) => (
                    <option key={p} value={p}>
                      {definitions?.[p]?.label || p}
                    </option>
                  ))}
                </select>
              </label>

              <GatewayForm
                def={definitions?.[addProvider] || {}}
                draft={addDraft}
                onPatch={updateAddDraft}
                onCredential={updateAddCredential}
                onCheckoutOption={updateAddCheckoutOption}
              />

              {addResult ? (
                <div
                  className={cx(
                    "rounded-xl border px-4 py-3 text-sm",
                    addResult.ok
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-rose-200 bg-rose-50 text-rose-700"
                  )}
                >
                  {addResult.message}
                </div>
              ) : null}
            </div>

            <div className="px-5 py-4 border-t border-slate-200 bg-white flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => testProvider(addProvider, addDraft, { fromAdd: true })}
                disabled={addTesting || !addProvider}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <FlaskConical size={14} />
                {addTesting ? "Testing..." : "Test Credentials"}
              </button>
              <button
                type="button"
                onClick={() => saveProvider(addProvider, addDraft, { asAdd: true })}
                disabled={addSaving || !addProvider}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#D2042D] px-4 py-2 text-sm font-semibold text-white hover:bg-[#b80326] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Plus size={14} />
                {addSaving ? "Adding..." : "Add Gateway"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editOpen && activeProvider ? (
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{activeProvider}</p>
                <h3 className="mt-1 text-lg font-bold text-slate-900">
                  Edit Gateway: {activeDef?.label || activeProvider}
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Update credentials, charge handling, and billing-country availability.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white p-2 text-slate-700 hover:bg-slate-50"
              >
                <X size={15} />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[76vh] overflow-y-auto">
              <GatewayForm
                def={activeDef}
                draft={activeDraft}
                onPatch={(patch) => updateDraft(activeProvider, patch)}
                onCredential={(key, value) => updateCredential(activeProvider, key, value)}
                onCheckoutOption={(key, value) => updateCheckoutOption(activeProvider, key, value)}
              />

              {activeResult ? (
                <div
                  className={cx(
                    "rounded-xl border px-4 py-3 text-sm",
                    activeResult.ok
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-rose-200 bg-rose-50 text-rose-700"
                  )}
                >
                  {activeResult.message}
                </div>
              ) : null}
            </div>

            <div className="px-5 py-4 border-t border-slate-200 bg-white flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => testProvider(activeProvider, activeDraft)}
                disabled={testingProvider === activeProvider}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <FlaskConical size={14} />
                {testingProvider === activeProvider ? "Testing..." : "Test Credentials"}
              </button>
              <button
                type="button"
                onClick={() => saveProvider(activeProvider, activeDraft)}
                disabled={savingProvider === activeProvider}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#D2042D] px-4 py-2 text-sm font-semibold text-white hover:bg-[#b80326] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Save size={14} />
                {savingProvider === activeProvider ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SectionTab({ active, onClick, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition",
        active
          ? "border-[#D2042D] bg-[#D2042D]/5 text-[#D2042D]"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function CountryMultiSelect({ valueText, onChange }) {
  const [query, setQuery] = useState("");
  const selectedCodes = useMemo(() => sortCountryCodes(parseCommaList(valueText)), [valueText]);
  const selectedSet = useMemo(() => new Set(selectedCodes), [selectedCodes]);
  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    return ALL_COUNTRY_OPTIONS.filter((country) => {
      if (selectedSet.has(country.code)) return false;
      if (!q) return true;
      return country.name.toLowerCase().includes(q) || country.code.toLowerCase().includes(q);
    }).slice(0, 80);
  }, [query, selectedSet]);

  const writeCodes = (codes) => onChange(toCommaList(sortCountryCodes(codes)));
  const addCountry = (code) => {
    const next = String(code || "").toUpperCase();
    if (!next || selectedSet.has(next)) return;
    writeCodes([...selectedCodes, next]);
  };
  const removeCountry = (code) => {
    const next = String(code || "").toUpperCase();
    writeCodes(selectedCodes.filter((c) => c !== next));
  };

  return (
    <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-slate-600">Allowed Billing Countries</p>
          <p className="mt-1 text-xs text-slate-500">
            Empty selection allows all billing countries for this gateway.
          </p>
        </div>
        <button
          type="button"
          onClick={() => writeCodes([])}
          className="inline-flex w-fit items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          Clear
        </button>
      </div>

      <div className="mt-3 min-h-[42px] rounded-lg border border-slate-200 bg-slate-50 p-2">
        {selectedCodes.length ? (
          <div className="flex flex-wrap gap-2">
            {selectedCodes.map((code) => (
              <span
                key={code}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700"
              >
                {countryLabel(code)} ({code})
                <button
                  type="button"
                  onClick={() => removeCountry(code)}
                  className="text-slate-400 hover:text-rose-600"
                  aria-label={`Remove ${countryLabel(code)}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="px-1 py-1 text-xs font-semibold text-slate-500">All billing countries</p>
        )}
      </div>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search country to allow..."
        className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
      />

      <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-white">
        {filtered.length ? (
          filtered.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => addCountry(country.code)}
              className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-800 last:border-b-0 hover:bg-slate-50"
            >
              <span className="font-semibold">{country.name}</span>
              <span className="text-xs font-semibold text-slate-500">{country.code}</span>
            </button>
          ))
        ) : (
          <div className="px-3 py-4 text-sm text-slate-500">No countries found.</div>
        )}
      </div>
    </div>
  );
}

function GatewayIconPreview({ iconUrl, size = "sm" }) {
  const boxClass =
    size === "lg"
      ? "h-12 w-12 rounded-xl"
      : "h-8 w-8 rounded-lg";
  const iconSize = size === "lg" ? 20 : 15;

  return (
    <span
      className={cx(
        "flex shrink-0 items-center justify-center overflow-hidden border border-slate-200 bg-slate-50 text-slate-500",
        boxClass
      )}
    >
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          className="h-full w-full object-contain"
          loading="lazy"
        />
      ) : (
        <CreditCard size={iconSize} />
      )}
    </span>
  );
}

function GatewayIconField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setBusy(true);
    setError("");
    try {
      const compressed = await compressGatewayIcon(file);
      onChange(compressed);
    } catch (e) {
      setError(e?.message || "Could not compress icon.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <GatewayIconPreview iconUrl={value} size="lg" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-600">Gateway icon</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              Upload PNG, JPG, or WebP. The system stores a compressed checkout icon.
            </p>
            {value ? (
              <p className="mt-1 text-xs font-semibold text-emerald-700">
                Stored size: {gatewayIconSizeLabel(value)}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <label
            className={cx(
              "inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-900 hover:bg-slate-50",
              busy ? "pointer-events-none opacity-60" : ""
            )}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
            {busy ? "Compressing..." : value ? "Replace" : "Upload"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onFileChange}
              className="hidden"
              disabled={busy}
            />
          </label>
          {value ? (
            <button
              type="button"
              onClick={() => onChange("")}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Trash2 size={13} />
              Remove
            </button>
          ) : null}
        </div>
      </div>
      {error ? (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function GatewayForm({ def, draft, onPatch, onCredential, onCheckoutOption }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Toggle
          label="Enabled"
          checked={Boolean(draft.enabled)}
          onChange={(v) => onPatch({ enabled: v })}
        />
        <Toggle
          label="Sandbox"
          checked={Boolean(draft.sandbox)}
          onChange={(v) => onPatch({ sandbox: v })}
        />
        <Toggle
          label="Simulation"
          checked={Boolean(draft.simulationMode)}
          onChange={(v) => onPatch({ simulationMode: v })}
        />
        <Toggle
          label="Require Webhooks"
          checked={Boolean(draft?.checkoutOptions?.requireWebhooks)}
          onChange={(v) => onCheckoutOption("requireWebhooks", v)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field
          label="Customer Display Name"
          value={draft.displayName}
          help={'Shown to customers at checkout, e.g. "Card / MFS / iBanking".'}
          onChange={(v) => onPatch({ displayName: v })}
        />
        <Field
          label="Manual Review Threshold"
          type="number"
          value={draft?.checkoutOptions?.manualReviewThreshold}
          onChange={(v) => onCheckoutOption("manualReviewThreshold", Number(v || 0))}
        />
      </div>

      <Field
        label="Description"
        value={draft.description}
        onChange={(v) => onPatch({ description: v })}
      />

      <GatewayIconField
        value={draft.iconUrl}
        onChange={(iconUrl) => onPatch({ iconUrl })}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CountryMultiSelect
          valueText={draft.allowedCountriesText}
          onChange={(v) => onPatch({ allowedCountriesText: v })}
        />
        <Field
          label="Allowed Currencies (comma separated)"
          value={draft.allowedCurrenciesText}
          onChange={(v) => onPatch({ allowedCurrenciesText: v })}
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Credentials & Advanced Fields
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(Array.isArray(def?.fields) ? def.fields : []).map((f) => {
            const value = draft?.credentials?.[f.key] ?? "";
            const isTextarea = f.type === "textarea";
            const isBoolean = f.type === "boolean";
            const isSelect = f.type === "select";
            if (isBoolean) {
              const help =
                f.key === "payWithCharge"
                  ? "On: merchant pays PayStation charges (pay_with_charge=1). Off: customer pays charges (pay_with_charge=0)."
                  : "";
              return (
                <Toggle
                  key={f.key}
                  label={f.label}
                  help={help}
                  checked={Boolean(value)}
                  onChange={(v) => onCredential(f.key, v)}
                />
              );
            }

            return (
              <label key={f.key} className={cx("block", isTextarea ? "sm:col-span-2" : "")}>
                <span className="text-xs font-semibold text-slate-600">{f.label}</span>
                {isSelect ? (
                  <select
                    value={value}
                    onChange={(e) => onCredential(f.key, e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
                  >
                    {(Array.isArray(f.options) ? f.options : []).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : isTextarea ? (
                  <textarea
                    rows={3}
                    value={value}
                    onChange={(e) => onCredential(f.key, e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
                  />
                ) : (
                  <input
                    type={f.type === "password" ? "password" : "text"}
                    value={value}
                    placeholder={f.secret ? "__configured__" : ""}
                    onChange={(e) => onCredential(f.key, e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
                  />
                )}
              </label>
            );
          })}
        </div>
      </div>
    </>
  );
}

function Field({ label, value, onChange, type = "text", help = "" }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
      />
      {help ? <span className="mt-1 block text-xs leading-relaxed text-slate-500">{help}</span> : null}
    </label>
  );
}

function Toggle({ label, checked, onChange, help = "" }) {
  return (
    <label className="inline-flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800">
      <span className="min-w-0">
        <span className="font-semibold">{label}</span>
        {help ? (
          <span className="mt-1 block text-xs leading-relaxed text-slate-500">{help}</span>
        ) : null}
      </span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={cx(
          "h-6 w-11 shrink-0 rounded-full border transition relative",
          checked ? "bg-[#D2042D] border-[#D2042D]" : "bg-slate-200 border-slate-300"
        )}
        aria-pressed={checked}
      >
        <span
          className={cx(
            "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition",
            checked ? "left-[21px]" : "left-0.5"
          )}
        />
      </button>
    </label>
  );
}
